import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Badge, ProgressBar, Alert, Accordion, Modal, Spinner, ListGroup, Tabs, Tab } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

const BookLaunchChecklist = () => {
  const navigate = useNavigate();
  const [launchDate, setLaunchDate] = useState('');
  const [checkedItems, setCheckedItems] = useState({});
  const [showSaved, setShowSaved] = useState(false);

  // Asset generator state
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [assetType, setAssetType] = useState(''); // bonus-chapters, worksheet, checklist, quickstart
  const [assetLoading, setAssetLoading] = useState(false);
  const [assetResult, setAssetResult] = useState(null);
  const [assetError, setAssetError] = useState('');

  // IP data for generators
  const [ipData, setIpData] = useState({ frameworks: [], processes: [], books: [] });
  const [selectedFramework, setSelectedFramework] = useState('');
  const [selectedProcess, setSelectedProcess] = useState('');
  const [selectedBook, setSelectedBook] = useState('');

  // Calendar sync state
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Load saved state on mount
  useEffect(() => {
    const saved = localStorage.getItem('bookLaunchChecklist');
    if (saved) {
      const data = JSON.parse(saved);
      setCheckedItems(data.checkedItems || {});
      setLaunchDate(data.launchDate || '');
    }
    loadIpData();
  }, []);

  const loadIpData = async () => {
    try {
      const response = await fetch('/api/authority-engineer/book/assets/ip-list', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setIpData(data);
        }
      }
    } catch (err) {
      console.error('Error loading IP data:', err);
    }
  };

  const openAssetGenerator = (type) => {
    setAssetType(type);
    setAssetResult(null);
    setAssetError('');
    setSelectedFramework('');
    setSelectedProcess('');
    setSelectedBook('');
    setShowAssetModal(true);
  };

  const generateAsset = async () => {
    setAssetLoading(true);
    setAssetError('');
    setAssetResult(null);

    try {
      let endpoint = '';
      let body = {};

      switch (assetType) {
        case 'bonus-chapters':
          endpoint = '/api/authority-engineer/book/bonus-chapters';
          body = { book_id: selectedBook || undefined };
          break;
        case 'worksheet':
          if (!selectedFramework) {
            setAssetError('Please select a framework');
            setAssetLoading(false);
            return;
          }
          endpoint = '/api/authority-engineer/book/worksheet';
          body = { framework_id: parseInt(selectedFramework) };
          break;
        case 'checklist':
          if (!selectedProcess) {
            setAssetError('Please select a process');
            setAssetLoading(false);
            return;
          }
          endpoint = '/api/authority-engineer/book/checklist';
          body = { process_id: parseInt(selectedProcess) };
          break;
        case 'quickstart':
          if (!selectedFramework) {
            setAssetError('Please select a framework');
            setAssetLoading(false);
            return;
          }
          endpoint = '/api/authority-engineer/book/quickstart';
          body = { framework_id: parseInt(selectedFramework) };
          break;
        default:
          setAssetError('Unknown asset type');
          setAssetLoading(false);
          return;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (data.success) {
        setAssetResult(data);
      } else {
        setAssetError(data.error || 'Failed to generate asset');
      }
    } catch (err) {
      setAssetError('Failed to connect to server');
    } finally {
      setAssetLoading(false);
    }
  };

  const downloadMarkdown = (content, filename) => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
  };

  const syncToCalendar = async () => {
    if (!launchDate) {
      alert('Please set a launch date first');
      return;
    }

    setSyncLoading(true);
    setSyncResult(null);

    try {
      const response = await fetch('/api/calendar/book-launch-sync', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ launch_date: launchDate })
      });

      const data = await response.json();
      if (data.success) {
        setSyncResult({
          success: true,
          message: data.message,
          tasksCreated: data.tasks_created
        });
      } else {
        setSyncResult({
          success: false,
          message: data.error || 'Failed to sync to calendar'
        });
      }
    } catch (err) {
      setSyncResult({
        success: false,
        message: 'Failed to connect to server'
      });
    } finally {
      setSyncLoading(false);
    }
  };

  const clearCalendarTasks = async () => {
    if (!window.confirm('Remove all book launch tasks from your calendar?')) return;

    setSyncLoading(true);
    try {
      const response = await fetch('/api/calendar/book-launch-clear', {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        setSyncResult({
          success: true,
          message: data.message
        });
      }
    } catch (err) {
      console.error('Error clearing calendar:', err);
    } finally {
      setSyncLoading(false);
    }
  };

  // Save state when it changes
  const saveProgress = () => {
    localStorage.setItem('bookLaunchChecklist', JSON.stringify({
      checkedItems,
      launchDate
    }));
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 2000);
  };

  const toggleItem = (phaseKey, itemIndex) => {
    const key = `${phaseKey}-${itemIndex}`;
    setCheckedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isChecked = (phaseKey, itemIndex) => {
    return checkedItems[`${phaseKey}-${itemIndex}`] || false;
  };

  const getPhaseProgress = (phaseKey, items) => {
    const total = items.length;
    const completed = items.filter((_, idx) => isChecked(phaseKey, idx)).length;
    return { completed, total, percent: Math.round((completed / total) * 100) };
  };

  const getTotalProgress = () => {
    const allItems = Object.values(phases).flat();
    const total = allItems.length;
    const completed = Object.values(checkedItems).filter(Boolean).length;
    return { completed, total, percent: Math.round((completed / total) * 100) };
  };

  const getDeadlineDate = (monthsBefore) => {
    if (!launchDate) return null;
    const launch = new Date(launchDate);
    const deadline = new Date(launch);
    deadline.setMonth(deadline.getMonth() - monthsBefore);
    return deadline;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const phases = {
    'phase1': [
      { text: 'Define your launch goals: book sales AND email subscribers, course/consulting leads, speaking inquiries, or IP licensing opportunities', ipStrong: true },
      { text: 'Create a full launch plan: timeline, budget, platforms (Amazon, wide distribution, etc.), pricing strategy', ipStrong: false },
      { text: 'Position the book as an entry point/loss leader to your broader IP ecosystem (e.g., "Read the book → get free tools → join the course")', ipStrong: true },
      { text: 'Research and build a media list: podcasts, blogs, magazines, LinkedIn influencers in your niche', ipStrong: false },
      { text: 'Seek endorsements/blurbs from recognized experts who can validate your original IP', ipStrong: true },
      { text: 'Build or update your author website with clear branding, about page, contact form, and blog', ipStrong: false },
      { text: 'Create dedicated landing pages for email sign-ups and bonus delivery (e.g., "Get the Full IP Toolkit")', ipStrong: true },
      { text: 'Develop a suite of high-value lead magnets tied directly to your IP: worksheets, templates, checklists, quizzes, assessments, quick-start guides, infographics of your framework', ipStrong: true },
      { text: 'Start aggressively growing your email list using the lead magnets above', ipStrong: false },
      { text: 'Prepare Advance Reader Copies (ARCs) for beta readers and reviewers', ipStrong: false },
      { text: 'Refine your author brand: professional bio, headshots, consistent messaging around your unique IP', ipStrong: false },
      { text: 'Begin content marketing to establish authority: guest posts, blog series, or podcast appearances teasing elements of your IP without giving everything away', ipStrong: true },
      { text: 'Consider trademarking/copyrighting key IP elements (framework name, branded tools, etc.) for credibility and protection', ipStrong: true }
    ],
    'phase2': [
      { text: 'Design promotional graphics: book mockups, quote cards, infographics/visuals of your IP framework, social banners', ipStrong: true },
      { text: 'Plan and schedule social media content (LinkedIn often outperforms for non-fiction IP books)', ipStrong: false },
      { text: 'Set up pre-order pages with incentives (discounts + exclusive bonuses)', ipStrong: false },
      { text: 'Create an extensive "IP Resource Vault" or bonus bundle: extra chapters, audio summaries, video breakdowns, case studies, swipe files, advanced templates', ipStrong: true },
      { text: 'Build secure download pages integrated with your email system for automated bonus delivery', ipStrong: true },
      { text: 'Order print proofs/galleys for quality checks and promo photos', ipStrong: false },
      { text: 'Pitch interviews, guest posts, and cover reveals', ipStrong: false },
      { text: 'Set up email automations/funnels: welcome sequence delivering bonuses, nurturing sequence teasing paid IP extensions (course, coaching, etc.)', ipStrong: true },
      { text: 'Form a street team or launch team of enthusiastic readers/experts', ipStrong: false },
      { text: 'Host early webinars or free challenges teaching a mini-version of your IP to build hype and collect emails', ipStrong: true }
    ],
    'phase3': [
      { text: 'Distribute ARCs widely (NetGalley, BookSirens, personal list) for early reviews', ipStrong: false },
      { text: 'Run PR campaigns: pitch podcasts (huge for non-fiction authority), blogs, niche media', ipStrong: true },
      { text: 'Schedule paid ads (Amazon, Facebook, LinkedIn) focused on lead magnets and pre-orders', ipStrong: false },
      { text: 'Plan launch events: virtual masterclass/webinar on your framework, live Q&A, or in-person if feasible', ipStrong: true },
      { text: 'Create a press kit: bio, synopsis, high-res images, sample chapters, IP overview one-pager', ipStrong: true },
      { text: 'Run giveaways and promotions (Goodreads, social media)', ipStrong: false },
      { text: 'Test all landing pages, email capture forms, and download delivery', ipStrong: false },
      { text: 'Engage deeply in niche communities (LinkedIn groups, Facebook groups, forums) sharing value from your IP', ipStrong: true }
    ],
    'phase4': [
      { text: 'Announce the launch across all channels: newsletter blast, social media, website update', ipStrong: false },
      { text: 'Host your main launch event (virtual party, framework masterclass, live reading/Q&A)', ipStrong: false },
      { text: 'Monitor sales rankings, reviews, and engagement in real time', ipStrong: false },
      { text: 'Run flash promotions or limited-time bonus upgrades', ipStrong: false },
      { text: 'Send personalized thank-you emails with bonus access and next-step offers', ipStrong: true }
    ],
    'phase5': [
      { text: 'Encourage and follow up on reviews (email sequence, social prompts)', ipStrong: false },
      { text: 'Launch ascension campaigns: promote paid products built on your IP (online course, group coaching, templates shop, etc.)', ipStrong: true },
      { text: 'Continue running ads and content marketing', ipStrong: false },
      { text: 'Update book pages and website with new reviews, testimonials, and media mentions', ipStrong: false },
      { text: 'Analyze performance: sales, email growth, bonus downloads, ad ROI — refine for future launches', ipStrong: false },
      { text: 'Explore IP expansion: speaking gigs, licensing opportunities, partnerships', ipStrong: true },
      { text: 'Tease your next book or IP evolution to keep the audience engaged', ipStrong: true },
      { text: 'Celebrate milestones publicly (e.g., "#1 in category" or "5000 downloads of the toolkit")', ipStrong: false }
    ]
  };

  const phaseInfo = {
    'phase1': {
      title: '6-12 Months Before Launch',
      subtitle: 'Foundation & Strategy',
      monthsBefore: 12,
      icon: '🎯'
    },
    'phase2': {
      title: '3-6 Months Before Launch',
      subtitle: 'Asset Creation & Testing',
      monthsBefore: 6,
      icon: '🛠️'
    },
    'phase3': {
      title: '1-3 Months Before Launch',
      subtitle: 'Ramp-Up & Visibility',
      monthsBefore: 3,
      icon: '📣'
    },
    'phase4': {
      title: 'Launch Week',
      subtitle: 'Execute & Celebrate',
      monthsBefore: 0,
      icon: '🚀'
    },
    'phase5': {
      title: '1-6 Months After Launch',
      subtitle: 'Sustain & Ascend',
      monthsBefore: -3,
      icon: '📈'
    }
  };

  const totalProgress = getTotalProgress();

  return (
    <Container fluid className="py-4 px-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <Button
            variant="link"
            className="p-0 mb-2"
            onClick={() => navigate('/command-center/authority-engineer/book')}
          >
            ← Back to Book Builder
          </Button>
          <h1>Master Book Launch Checklist</h1>
          <p className="text-muted mb-0">
            For Non-Fiction IP Books • Items marked <Badge bg="warning" text="dark" className="ms-1">IP-Strong</Badge> are especially important for authority-building books
          </p>
        </div>
        <Button variant="primary" onClick={saveProgress}>
          Save Progress
        </Button>
      </div>

      {showSaved && (
        <Alert variant="success" className="mb-3">
          Progress saved!
        </Alert>
      )}

      {/* Launch Date & Overall Progress */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="h-100">
            <Card.Body>
              <h6 className="text-muted mb-2">Target Launch Date</h6>
              <Form.Control
                type="date"
                value={launchDate}
                onChange={(e) => { setLaunchDate(e.target.value); setSyncResult(null); }}
                className="mb-2"
              />
              {launchDate && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-2">
                    {Math.ceil((new Date(launchDate) - new Date()) / (1000 * 60 * 60 * 24))} days until launch
                  </small>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={syncToCalendar}
                      disabled={syncLoading}
                    >
                      {syncLoading ? <Spinner size="sm" /> : '📅 Sync to Calendar'}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={clearCalendarTasks}
                      disabled={syncLoading}
                      title="Remove book launch tasks from calendar"
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              )}
              {syncResult && (
                <Alert variant={syncResult.success ? 'success' : 'danger'} className="py-2 mb-0 small">
                  {syncResult.message}
                  {syncResult.tasksCreated && (
                    <div className="mt-1">
                      <a href="/command-center/calendars" className="alert-link">View Calendar →</a>
                    </div>
                  )}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={8}>
          <Card className="h-100" style={{ backgroundColor: colors.greyDarkest, color: 'white' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0" style={{ color: colors.goldLight }}>Overall Progress</h6>
                <span className="h4 mb-0">{totalProgress.completed} / {totalProgress.total}</span>
              </div>
              <ProgressBar
                now={totalProgress.percent}
                label={`${totalProgress.percent}%`}
                variant="warning"
                style={{ height: '25px' }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Phase Accordions */}
      <Accordion defaultActiveKey="phase1" alwaysOpen>
        {Object.entries(phases).map(([phaseKey, items]) => {
          const info = phaseInfo[phaseKey];
          const progress = getPhaseProgress(phaseKey, items);
          const deadline = getDeadlineDate(info.monthsBefore);

          return (
            <Accordion.Item eventKey={phaseKey} key={phaseKey} className="mb-3">
              <Accordion.Header>
                <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                  <div>
                    <span className="me-2" style={{ fontSize: '1.3rem' }}>{info.icon}</span>
                    <strong>{info.title}</strong>
                    <span className="text-muted ms-2">— {info.subtitle}</span>
                    {deadline && (
                      <Badge bg="secondary" className="ms-2">
                        {info.monthsBefore >= 0 ? 'Start by' : 'After'}: {formatDate(deadline)}
                      </Badge>
                    )}
                  </div>
                  <div className="text-end" style={{ minWidth: '120px' }}>
                    <Badge bg={progress.percent === 100 ? 'success' : 'primary'}>
                      {progress.completed}/{progress.total}
                    </Badge>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <ProgressBar
                  now={progress.percent}
                  variant={progress.percent === 100 ? 'success' : 'info'}
                  className="mb-3"
                  style={{ height: '8px' }}
                />
                {items.map((item, idx) => (
                  <div
                    key={idx}
                    className="d-flex align-items-start py-2 border-bottom"
                    style={{
                      opacity: isChecked(phaseKey, idx) ? 0.6 : 1,
                      textDecoration: isChecked(phaseKey, idx) ? 'line-through' : 'none'
                    }}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={isChecked(phaseKey, idx)}
                      onChange={() => toggleItem(phaseKey, idx)}
                      className="me-3 mt-1"
                      style={{ minWidth: '20px' }}
                    />
                    <div className="flex-grow-1">
                      {item.text}
                      {item.ipStrong && (
                        <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.7rem' }}>
                          IP-Strong
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>

      {/* Strategy Note */}
      <Card className="mt-4 border-0" style={{ backgroundColor: '#fff8e6' }}>
        <Card.Body>
          <h6 style={{ color: colors.goldDark }}>
            <span className="me-2">💡</span>
            IP Book Launch Strategy
          </h6>
          <p className="small text-muted mb-0">
            This checklist is tailored for non-fiction books that showcase your original intellectual property —
            a unique framework, system, methodology, or tools. The book is positioned not just for sales, but as a
            powerful <strong>lead generator</strong> and <strong>authority builder</strong> that funnels readers into your
            email list, community, and higher-value offerings (courses, coaching, workshops, licensed tools, etc.).
          </p>
        </Card.Body>
      </Card>

      {/* Bonus Asset Generators */}
      <Card className="mt-4">
        <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <h5 className="mb-0">Bonus Asset Generators</h5>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-3">
            Generate downloadable assets from your IP to include in your book's bonus bundle.
          </p>
          <Row>
            <Col md={3} className="mb-3">
              <Card
                className="h-100 text-center py-3"
                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                onClick={() => openAssetGenerator('bonus-chapters')}
                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'none'}
              >
                <Card.Body>
                  <div style={{ fontSize: '2rem' }}>📚</div>
                  <h6>Bonus Chapters</h6>
                  <small className="text-muted">Extra chapter ideas from your book outline</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-3">
              <Card
                className="h-100 text-center py-3"
                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                onClick={() => openAssetGenerator('worksheet')}
                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'none'}
              >
                <Card.Body>
                  <div style={{ fontSize: '2rem' }}>📝</div>
                  <h6>Worksheets</h6>
                  <small className="text-muted">Printable exercises from your frameworks</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-3">
              <Card
                className="h-100 text-center py-3"
                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                onClick={() => openAssetGenerator('checklist')}
                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'none'}
              >
                <Card.Body>
                  <div style={{ fontSize: '2rem' }}>✅</div>
                  <h6>Checklists</h6>
                  <small className="text-muted">Quick-reference guides from your processes</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-3">
              <Card
                className="h-100 text-center py-3"
                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                onClick={() => openAssetGenerator('quickstart')}
                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'none'}
              >
                <Card.Body>
                  <div style={{ fontSize: '2rem' }}>🚀</div>
                  <h6>Quick-Start Guide</h6>
                  <small className="text-muted">1-page starter from your framework</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <hr />
          <Row>
            <Col md={6} className="mb-3">
              <Card
                className="h-100 text-center py-3"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/command-center/authority-engineer/amazon-publishing')}
              >
                <Card.Body>
                  <div style={{ fontSize: '1.5rem' }}>📖</div>
                  <h6 className="mb-1">Amazon Publishing Guide</h6>
                  <small className="text-muted">KDP setup, categories, keywords, spine calculator</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-3">
              <Card
                className="h-100 text-center py-3"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/command-center/authority-engineer/book')}
              >
                <Card.Body>
                  <div style={{ fontSize: '1.5rem' }}>📕</div>
                  <h6 className="mb-1">Book Builder</h6>
                  <small className="text-muted">Plan and outline your book from your IP</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Asset Generator Modal */}
      <Modal show={showAssetModal} onHide={() => setShowAssetModal(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>
            {assetType === 'bonus-chapters' && 'Generate Bonus Chapter Ideas'}
            {assetType === 'worksheet' && 'Generate Worksheet'}
            {assetType === 'checklist' && 'Generate Checklist'}
            {assetType === 'quickstart' && 'Generate Quick-Start Guide'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {assetError && <Alert variant="danger">{assetError}</Alert>}

          {!assetResult ? (
            <>
              {/* Selection UI */}
              {assetType === 'bonus-chapters' && (
                <div>
                  <p className="text-muted">
                    Generate 5 bonus chapter ideas based on your book outline. These complement your main book
                    and make excellent bonuses for readers who sign up.
                  </p>
                  {ipData.books.length > 0 ? (
                    <Form.Group className="mb-3">
                      <Form.Label>Select Book (optional - uses most recent if not selected)</Form.Label>
                      <Form.Select value={selectedBook} onChange={(e) => setSelectedBook(e.target.value)}>
                        <option value="">Most recent book</option>
                        {ipData.books.map(b => (
                          <option key={b.id} value={b.id}>{b.title}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  ) : (
                    <Alert variant="warning">
                      No book outlines found. <a href="/command-center/authority-engineer/book">Create a book outline first</a>.
                    </Alert>
                  )}
                </div>
              )}

              {assetType === 'worksheet' && (
                <div>
                  <p className="text-muted">
                    Create a printable worksheet that helps readers apply your framework. Includes
                    fill-in-the-blank exercises and reflection questions.
                  </p>
                  {ipData.frameworks.length > 0 ? (
                    <Form.Group className="mb-3">
                      <Form.Label>Select Framework</Form.Label>
                      <Form.Select value={selectedFramework} onChange={(e) => setSelectedFramework(e.target.value)}>
                        <option value="">Choose a framework...</option>
                        {ipData.frameworks.map(f => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  ) : (
                    <Alert variant="warning">
                      No frameworks found. <a href="/command-center/authority-engineer/extract">Extract your IP first</a>.
                    </Alert>
                  )}
                </div>
              )}

              {assetType === 'checklist' && (
                <div>
                  <p className="text-muted">
                    Create a quick-reference checklist from your documented process. Includes
                    pre-checklist items, step-by-step tasks, and success criteria.
                  </p>
                  {ipData.processes.length > 0 ? (
                    <Form.Group className="mb-3">
                      <Form.Label>Select Process</Form.Label>
                      <Form.Select value={selectedProcess} onChange={(e) => setSelectedProcess(e.target.value)}>
                        <option value="">Choose a process...</option>
                        {ipData.processes.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  ) : (
                    <Alert variant="warning">
                      No processes found. <a href="/command-center/authority-engineer/processes">Build a process first</a>.
                    </Alert>
                  )}
                </div>
              )}

              {assetType === 'quickstart' && (
                <div>
                  <p className="text-muted">
                    Create a 1-page quick-start guide that gets readers their first win in 15 minutes.
                    Perfect for readers who want immediate action steps.
                  </p>
                  {ipData.frameworks.length > 0 ? (
                    <Form.Group className="mb-3">
                      <Form.Label>Select Framework</Form.Label>
                      <Form.Select value={selectedFramework} onChange={(e) => setSelectedFramework(e.target.value)}>
                        <option value="">Choose a framework...</option>
                        {ipData.frameworks.map(f => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  ) : (
                    <Alert variant="warning">
                      No frameworks found. <a href="/command-center/authority-engineer/extract">Extract your IP first</a>.
                    </Alert>
                  )}
                </div>
              )}

              <div className="text-end">
                <Button variant="secondary" className="me-2" onClick={() => setShowAssetModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="dark"
                  style={{ backgroundColor: colors.greyDarkest }}
                  onClick={generateAsset}
                  disabled={assetLoading}
                >
                  {assetLoading ? <><Spinner size="sm" className="me-2" />Generating...</> : 'Generate'}
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Results UI */}
              {assetType === 'bonus-chapters' && assetResult.result && (
                <div>
                  <Alert variant="success">
                    <strong>Bundle Name:</strong> {assetResult.result.bundle_name}
                  </Alert>
                  {assetResult.result.marketing_angle && (
                    <p className="text-muted small mb-3">
                      <strong>Marketing Angle:</strong> {assetResult.result.marketing_angle}
                    </p>
                  )}
                  <ListGroup className="mb-3">
                    {assetResult.result.bonus_chapters?.map((ch, idx) => (
                      <ListGroup.Item key={idx}>
                        <div className="d-flex justify-content-between">
                          <strong>{ch.title}</strong>
                          <Badge bg="secondary">{ch.type}</Badge>
                        </div>
                        <p className="mb-1 small">{ch.description}</p>
                        <small className="text-muted">
                          <strong>Hook:</strong> {ch.hook} • {ch.estimated_length}
                        </small>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}

              {(assetType === 'worksheet' || assetType === 'checklist' || assetType === 'quickstart') && assetResult.result && (
                <div>
                  <Alert variant="success">
                    <strong>{assetResult.result.title || assetResult.framework_name || assetResult.process_name}</strong>
                  </Alert>

                  <Tabs defaultActiveKey="preview" className="mb-3">
                    <Tab eventKey="preview" title="Preview">
                      <div
                        className="border rounded p-3 bg-light"
                        style={{ maxHeight: '400px', overflowY: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}
                      >
                        {assetResult.result.markdown_version || JSON.stringify(assetResult.result, null, 2)}
                      </div>
                    </Tab>
                    <Tab eventKey="structured" title="Structured View">
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {assetType === 'quickstart' && assetResult.result.start_here && (
                          <div>
                            <p className="lead">{assetResult.result.tagline}</p>
                            <p>{assetResult.result.the_big_idea}</p>
                            <h6>Get Started:</h6>
                            <ListGroup className="mb-3">
                              {Object.entries(assetResult.result.start_here).map(([key, step]) => (
                                <ListGroup.Item key={key}>
                                  <strong>{step.action}</strong> <Badge bg="info">{step.time}</Badge>
                                  <br /><small className="text-muted">Result: {step.result}</small>
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                            <Alert variant="success" className="py-2">
                              <strong>Your First Win:</strong> {assetResult.result.your_first_win}
                            </Alert>
                            <Alert variant="warning" className="py-2">
                              <strong>Avoid:</strong> {assetResult.result.common_mistake}
                            </Alert>
                            <Alert variant="info" className="py-2">
                              <strong>Pro Tip:</strong> {assetResult.result.pro_tip}
                            </Alert>
                          </div>
                        )}
                        {assetType === 'worksheet' && assetResult.result.sections && (
                          <div>
                            <p>{assetResult.result.intro}</p>
                            {assetResult.result.sections.map((section, idx) => (
                              <Card key={idx} className="mb-2">
                                <Card.Header className="py-2"><strong>{section.heading}</strong></Card.Header>
                                <Card.Body className="py-2">
                                  <p className="small mb-2">{section.instructions}</p>
                                  {section.prompts?.map((p, i) => <div key={i} className="small text-muted">• {p}</div>)}
                                </Card.Body>
                              </Card>
                            ))}
                          </div>
                        )}
                        {assetType === 'checklist' && assetResult.result.sections && (
                          <div>
                            {assetResult.result.pre_checklist?.length > 0 && (
                              <Alert variant="info" className="py-2">
                                <strong>Before you start:</strong>
                                <ul className="mb-0 small">
                                  {assetResult.result.pre_checklist.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                              </Alert>
                            )}
                            {assetResult.result.sections.map((section, idx) => (
                              <div key={idx} className="mb-3">
                                <h6>{section.name}</h6>
                                {section.items?.map((item, i) => (
                                  <div key={i} className="d-flex align-items-start mb-1">
                                    <span className="me-2">☐</span>
                                    <span className={item.critical ? 'fw-bold' : ''}>
                                      {item.task}
                                      {item.critical && <Badge bg="danger" className="ms-1">Critical</Badge>}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Tab>
                  </Tabs>

                  <div className="d-flex gap-2 mt-3">
                    <Button
                      variant="primary"
                      onClick={() => downloadMarkdown(
                        assetResult.result.markdown_version || JSON.stringify(assetResult.result, null, 2),
                        `${assetType}-${Date.now()}.md`
                      )}
                    >
                      Download Markdown
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => copyToClipboard(assetResult.result.markdown_version || JSON.stringify(assetResult.result, null, 2))}
                    >
                      Copy to Clipboard
                    </Button>
                  </div>
                </div>
              )}

              <hr />
              <div className="text-end">
                <Button variant="outline-secondary" className="me-2" onClick={() => setAssetResult(null)}>
                  Generate Another
                </Button>
                <Button variant="secondary" onClick={() => setShowAssetModal(false)}>
                  Close
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default BookLaunchChecklist;
